/* ============================================================
   Mobile navigation drawer — Mobile only (< 1024px)
   ------------------------------------------------------------
   Single custom element that owns the ENTIRE mobile navigation:
     1. drawer shell — open/close, overlay, focus trap, Escape,
        tabindex management (merged from the former DrawerNavSection),
     2. accordion tree — the mnav-* structure rendered by
        snippets/mega-menu.liquid with context: 'mobile'.

   Completely separate from the desktop hover dropdown logic
   (assets/mega-menu-desktop.js). Utility helpers (setTabindex,
   toggleTabindex, getStyleProperty, addEventListeners) come from
   global.js which is loaded theme-wide.
   ============================================================ */

class MobileNavDrawer extends HTMLElement {
  constructor() {
    super();

    this.pageOverlayClass = "menu-drawer-overlay";
    this.triggerQuery = [
      ".wt-header__menu-trigger",
      ".wt-drawer__close",
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.menuToggleButton = document.querySelector(".wt-header__menu-trigger");
    this.closeButton = this.querySelector(".wt-drawer__close");
    this.isOpen = false;
    this.triggerElement = null;
    this.isAlwaysMobile = () => document.body.classList.contains("mobile-nav");
    this.desktopBreakpoint = 1024;
    this.isDesktop = () =>
      window.matchMedia(`(min-width: ${this.desktopBreakpoint}px)`).matches;
  }

  connectedCallback() {
    this.init();
  }

  _reduceMotion() {
    return (
      this._rmmq ||
        (this._rmmq = window.matchMedia("(prefers-reduced-motion: reduce)"))
    ).matches;
  }

  /* ---------- tabindex ---------- */

  handleTabindex() {
    const linksLvl1 = this.querySelectorAll('a[data-menu-level="1"]');
    if (!this.isAlwaysMobile()) {
      setTabindex(linksLvl1, this.isDesktop() ? "0" : "-1");
    }
  }

  updateAriaStateForTriggers() {
    const triggers = document.querySelectorAll(
      "[aria-controls='wt-drawer-nav']",
    );
    triggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", this.isOpen);
    });
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  /* ---------- drawer open / close ---------- */

  openMenu(e) {
    this.isOpen = true;
    this.triggerElement = e.currentTarget;
    this.closeButton.setAttribute("tabindex", "0");
    this.closeButton.focus();
    this.temporaryHideFocusVisible();
  }

  /* Reset every open accordion panel so the drawer always
     reopens in its collapsed state. */
  closeSubmenus() {
    // Reset the Jackery-style detail view if it is open
    const stage = this.querySelector("[data-mnav-stage]");
    const detailContainer = this.querySelector("[data-mnav-detail]");
    if (stage && stage.classList.contains("is-detail")) {
      stage.classList.remove("is-detail");
      stage.classList.remove("mnav-stage--anim");
      if (detailContainer) {
        detailContainer.hidden = true;
        detailContainer.setAttribute("aria-hidden", "true");
        detailContainer.innerHTML = "";
      }
    }
    // Root must be interactive again (inert is set while detail is open)
    const rootEl = stage && stage.querySelector("[data-mnav-root]");
    if (rootEl) rootEl.inert = false;

    this.querySelectorAll(".mnav__item--opened").forEach((item) => {
      item.classList.remove("mnav__item--opened");
      const link = item.querySelector(".mnav__link--parent");
      if (link) link.setAttribute("aria-expanded", "false");
      setTabindex(
        item.querySelectorAll('[data-menu-level="2"],[data-menu-level="3"]'),
        "-1",
      );
    });
  }

  closeMenu() {
    this.isOpen = false;
    this.triggerElement.focus();
    this.triggerElement = null;
    this.closeButton.setAttribute("tabindex", "-1");
    this.closeSubmenus();
    this.temporaryHideFocusVisible();
  }

  toggleMenu(e) {
    e.preventDefault();

    if (this.isOpen) {
      this.closeMenu(e);
    } else {
      this.openMenu(e);
    }

    // Promote the drawer to its own compositor layer only while the
    // slide transition is actually running. A permanent will-change
    // keeps a full-viewport layer in memory for the page's lifetime.
    if (!this._reduceMotion()) {
      this.classList.add("mnav--animating");
    }

    this.updateAriaStateForTriggers();

    // Level-1 links (nav items, footer links, locale button all carry
    // data-menu-level="1") — toggle their tabbability with the drawer.
    const linksLvl1 = this.querySelectorAll("[data-menu-level='1']");
    const localizationTriggers = this.querySelectorAll(
      ".wt-localization-trigger",
    );

    toggleTabindex(localizationTriggers);
    toggleTabindex(linksLvl1);
    this.toggleMenuButtonAttr();

    document.body.classList.toggle("menu-open");
    document.body.classList.toggle("menu-drawer-overlay-on");
  }

  toggleMenuButtonAttr() {
    const dataOpen =
      this.menuToggleButton.dataset.open === "true" ? "false" : "true";
    this.menuToggleButton.dataset.open = dataOpen;
  }

  /* ---------- focus trap ---------- */

  getFocusableElements() {
    // One DOM scan per call (the previous implementation queried and
    // filtered the whole drawer three times on every Tab keypress).
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const els = Array.from(
      this.querySelectorAll(focusableElementsSelector),
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);

    return {
      focusableElements: () => els,
      first: els[0],
      last: els[els.length - 1],
    };
  }

  /* ---------- accordion ---------- */

  initAccordion() {
    const parentLinks = this.querySelectorAll(".mnav__link--parent");

    parentLinks.forEach((link) => {
      // Mega items open the Jackery-style detail view instead of the accordion
      if (link.hasAttribute("data-mega-trigger")) return;

      link.addEventListener("click", (e) => {
        // Parent rows only expand/collapse their submenu — they never follow
        // the menu link. Navigating away on tap made the tree unusable.
        e.preventDefault();

        const item = link.closest(".mnav__item--parent");
        if (!item) return;
        const willOpen = !item.classList.contains("mnav__item--opened");

        // Close every other open panel
        this.querySelectorAll(".mnav__item--opened").forEach((open) => {
          if (open === item) return;
          open.classList.remove("mnav__item--opened");
          const otherLink = open.querySelector(".mnav__link--parent");
          if (otherLink) otherLink.setAttribute("aria-expanded", "false");
          setTabindex(
            open.querySelectorAll(
              '[data-menu-level="2"],[data-menu-level="3"]',
            ),
            "-1",
          );
        });

        item.classList.toggle("mnav__item--opened", willOpen);
        link.setAttribute("aria-expanded", willOpen ? "true" : "false");
        setTabindex(
          item.querySelectorAll(
            '[data-menu-level="2"],[data-menu-level="3"]',
          ),
          willOpen ? "0" : "-1",
        );
      });
    });

    // Level-1 links start tabbable; deeper links are reachable once opened.
    setTabindex(
      this.querySelectorAll('[data-menu-level="2"],[data-menu-level="3"]'),
      "-1",
    );
    setTabindex(this.querySelectorAll('a[data-menu-level="1"]'), "0");
  }

  /* ---------- Jackery-style mega detail view ---------- */

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  initMegaPanels() {
    const stage = this.querySelector("[data-mnav-stage]");
    const detailContainer = this.querySelector("[data-mnav-detail]");
    if (!stage || !detailContainer) return;

    this.querySelectorAll("[data-mega-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        // Same contract as the accordion: the whole row just opens the mega
        // detail view; it never follows the menu link.
        e.preventDefault();
        this.lastMegaTrigger = trigger;

        const handle = trigger.dataset.megaHandle;
        const titleEl = trigger.querySelector(".mnav__link-text");
        const title = titleEl
          ? titleEl.textContent.trim()
          : trigger.textContent.trim();
        const panel = this.querySelector(`#mnav-panel-${handle}`);
        if (!panel) return;

        this.openDetailView(title, panel);
      });
    });
  }

  openDetailView(title, sourcePanel) {
    const stage = this.querySelector("[data-mnav-stage]");
    const detailContainer = this.querySelector("[data-mnav-detail]");
    if (!stage || !detailContainer) return;

    // Theme icon set server-rendered in page-header.liquid (see
    // [data-mnav-back-icon]); read once and reuse for every open.
    if (this._backIconHtml === undefined) {
      const iconTpl = this.querySelector("[data-mnav-back-icon]");
      this._backIconHtml = iconTpl ? iconTpl.innerHTML : "";
    }

    const megaRoot = sourcePanel.querySelector(".mnav__mega");
    if (!megaRoot) return;

    const groups = Array.from(megaRoot.querySelectorAll(".mnav__group"));
    const shopAll = megaRoot.querySelector(".mnav__shopall");

    let tabsHtml = "";
    let gridsHtml = "";

    if (groups.length > 0) {
      tabsHtml = groups
        .map((g, i) => {
          const labelEl = g.querySelector(".mnav__group-label");
          const label = labelEl ? labelEl.textContent.trim() : "";
          const activeCls = i === 0 ? " mnav-detail__tab--active" : "";
          return `<button type="button" class="mnav-detail__tab${activeCls}" role="tab" aria-selected="${i === 0}" data-tab-index="${i}">${this.escapeHtml(label)}</button>`;
        })
        .join("");

      gridsHtml = groups
        .map((g, i) => {
          const viewAll = g.querySelector(".mnav__group-viewall");
          // Clone EVERY child except the group label (that label is already
          // the tab) and the view-all row (hoisted below). A group can hold
          // several card sub-groups, each rendered as its own
          // .mega-menu__card-group-header + .mnav__cards pair; grabbing only
          // the first .mnav__cards with querySelector dropped every later
          // sub-group, so a tab displayed a single product series.
          const bodyHtml = Array.from(g.children)
            .filter(
              (el) =>
                !el.classList.contains("mnav__group-label") && el !== viewAll,
            )
            .map((el) => el.outerHTML)
            .join("");
          const viewAllHtml = viewAll
            ? `<div class="mnav-detail__viewall">${viewAll.outerHTML}</div>`
            : "";
          return `<div class="mnav-detail__grid" role="tabpanel" data-grid-index="${i}"${i !== 0 ? " hidden" : ""}>${viewAllHtml}${bodyHtml}</div>`;
        })
        .join("");
    } else {
      // Cards-only panel (no groups): keep every card sub-group, not just the
      // first .mnav__cards. The panel-level CTA renders in the footer instead.
      const bodyHtml = Array.from(megaRoot.children)
        .filter((el) => !el.classList.contains("mnav__shopall"))
        .map((el) => el.outerHTML)
        .join("");
      gridsHtml = `<div class="mnav-detail__grid" data-grid-index="0">${bodyHtml}</div>`;
    }

    const ctaHtml = shopAll
      ? `<div class="mnav-detail__footer"><a href="${shopAll.getAttribute("href")}" class="mnav-detail__shopall">${this.escapeHtml(shopAll.textContent.trim())}</a></div>`
      : "";

    detailContainer.innerHTML = `
      <div class="mnav-detail">
        <div class="mnav-detail__header">
          <button type="button" class="mnav-detail__back" data-mega-back aria-label="Back">
            ${this._backIconHtml}
          </button>
          <span class="mnav-detail__title">${this.escapeHtml(title)}</span>
        </div>
        ${tabsHtml ? `<div class="mnav-detail__tabs" role="tablist">${tabsHtml}</div>` : ""}
        <div class="mnav-detail__content">${gridsHtml}</div>
        ${ctaHtml}
      </div>
    `;

    // Cloned card links inherit tabindex="-1" from the accordion source;
    // make them tabbable inside the detail view.
    detailContainer
      .querySelectorAll('[tabindex="-1"]')
      .forEach((el) => el.removeAttribute("tabindex"));

    // Tab switching
    const tabsScroller = detailContainer.querySelector(".mnav-detail__tabs");
    const activateTab = (tab) => {
      const idx = tab.dataset.tabIndex;
      detailContainer.querySelectorAll(".mnav-detail__tab").forEach((t) => {
        t.classList.remove("mnav-detail__tab--active");
        t.setAttribute("aria-selected", "false");
      });
      tab.classList.add("mnav-detail__tab--active");
      tab.setAttribute("aria-selected", "true");

      detailContainer
        .querySelectorAll(".mnav-detail__grid")
        .forEach((g) => (g.hidden = true));
      const grid = detailContainer.querySelector(
        `[data-grid-index="${idx}"]`,
      );
      if (grid) grid.hidden = false;

      // The strip is a horizontal scroller, so a tapped tab can end up
      // half-clipped at the edge. inline:'center' keeps it fully visible;
      // block:'nearest' makes sure no ancestor scrolls vertically (the tab
      // is already on screen).
      if (tabsScroller && tabsScroller.scrollWidth > tabsScroller.clientWidth) {
        tab.scrollIntoView({
          behavior: this._reduceMotion() ? "auto" : "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    };

    detailContainer.querySelectorAll("[data-tab-index]").forEach((tab) => {
      tab.addEventListener("click", () => activateTab(tab));
    });

    // Back button
    detailContainer
      .querySelector("[data-mega-back]")
      .addEventListener("click", () => {
        this.closeDetailView();
      });

    // While the detail view is open the root is shifted left and faded;
    // inert prevents keyboard/screen-reader focus leaking into it.
    const rootEl = stage.querySelector("[data-mnav-root]");
    if (rootEl) rootEl.inert = true;
    this.lastMegaTrigger.setAttribute("aria-expanded", "true");

    // Show
    detailContainer.hidden = false;
    detailContainer.setAttribute("aria-hidden", "false");
    // Promote the two sliding views for the transition only. The class
    // is dropped when the open transition ends and re-added by
    // closeDetailView() for the way back.
    if (!this._reduceMotion()) {
      stage.classList.add("mnav-stage--anim");
      const onOpenEnd = (ev) => {
        if (
          ev.target === detailContainer &&
          ev.propertyName === "transform"
        ) {
          stage.removeEventListener("transitionend", onOpenEnd);
          stage.classList.remove("mnav-stage--anim");
        }
      };
      stage.addEventListener("transitionend", onOpenEnd);
    }
    // Force reflow so the translateX(100%) -> 0 transition actually plays
    void detailContainer.offsetWidth;
    stage.classList.add("is-detail");

    const backBtn = detailContainer.querySelector("[data-mega-back]");
    if (backBtn) backBtn.focus();
  }

  closeDetailView() {
    const stage = this.querySelector("[data-mnav-stage]");
    const detailContainer = this.querySelector("[data-mnav-detail]");
    if (!stage || !detailContainer) return;

    // Re-promote for the slide-back transition; cleanup drops the class.
    if (!this._reduceMotion()) {
      stage.classList.add("mnav-stage--anim");
    }
    stage.classList.remove("is-detail");
    const rootEl = stage.querySelector("[data-mnav-root]");
    if (rootEl) rootEl.inert = false;

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      stage.removeEventListener("transitionend", cleanup);
      stage.classList.remove("mnav-stage--anim");
      detailContainer.hidden = true;
      detailContainer.setAttribute("aria-hidden", "true");
      detailContainer.innerHTML = "";
    };

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      cleanup();
    } else {
      stage.addEventListener("transitionend", cleanup, { once: true });
      setTimeout(cleanup, 400);
    }

    if (this.lastMegaTrigger) {
      this.lastMegaTrigger.setAttribute("aria-expanded", "false");
      this.lastMegaTrigger.focus();
      this.lastMegaTrigger = null;
    }
  }

  /* ---------- init ---------- */

  init() {
    if (!document.querySelector(`.${this.pageOverlayClass}`)) {
      const overlay = document.createElement("div");
      overlay.classList.add(this.pageOverlayClass);
      document.body.appendChild(overlay);
    }

    this.handleTabindex();

    // One breakpoint media query drives two behaviours:
    //   1. sync level-1 link tabindex with desktop/mobile
    //   2. force-close the drawer when crossing into desktop (rotating a
    //      tablet / dragging the window wider) — otherwise the body-level
    //      menu-open / overlay classes linger and lock the desktop page.
    // A matchMedia "change" fires only at the breakpoint crossing, unlike
    // "resize" which storms on mobile whenever the URL bar shows/hides.
    if (!this._breakpointBound) {
      const mq = window.matchMedia(
        `(min-width: ${this.desktopBreakpoint}px)`,
      );
      const onChange = (e) => {
        this.handleTabindex();
        if (e.matches && this.isOpen) this.closeButton.click();
      };
      if (mq.addEventListener) {
        mq.addEventListener("change", onChange);
      } else if (mq.addListener) {
        mq.addListener(onChange);
      }
      this._breakpointBound = true;
    }

    // Drop the temporary compositor promotion as soon as the drawer's
    // own slide finishes (bubbled transitions of inner elements ignored).
    if (!this._transitionBound) {
      this.addEventListener("transitionend", (e) => {
        if (e.target === this && e.propertyName === "transform") {
          this.classList.remove("mnav--animating");
        }
      });
      this._transitionBound = true;
    }

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        const stage = this.querySelector("[data-mnav-stage]");
        if (stage && stage.classList.contains("is-detail")) {
          this.closeDetailView();
          e.stopPropagation();
          return;
        }
        if (this.isOpen) {
          this.toggleMenu(e);
        }
      }

      if (!this.isDesktop() || this.isAlwaysMobile()) {
        if (isTabPressed) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        this.toggleMenu(e);
      });
    });

    this.initAccordion();
    this.initMegaPanels();
  }
}

customElements.define("mobile-nav-drawer", MobileNavDrawer);
